'use client';

import { useState, useEffect } from 'react';
import DelegationFilters from './DelegationFilters';
import DelegationTable from './DelegationTable';
import DelegationDetailPanel from './DelegationDetailPanel';
import BulkActionsBar from './BulkActionsBar';
import DelegationStats from './DelegationStats';
import Icon from '@/components/ui/AppIcon';

interface FilterOptions {
  dateRange: string;
  delegationType: string;
  approvalStatus: string;
  department: string;
  savedView: string;
}

interface Delegation {
  id: string;
  delegator: {
    name: string;
    email: string;
    avatar: string;
    alt: string;
    department: string;
  };
  delegate: {
    name: string;
    email: string;
    avatar: string;
    alt: string;
    department: string;
  };
  scope: string;
  reason: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'revoked' | 'active';
  type: string;
  approver?: string;
  approvalDate?: string;
  workloadImpact: number;
}

const DelegationControlInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: 'all',
    delegationType: 'all',
    approvalStatus: 'all',
    department: 'all',
    savedView: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDelegations, setSelectedDelegations] = useState<string[]>([]);
  const [selectedDelegation, setSelectedDelegation] = useState<Delegation | null>(null);
  const [sortBy, setSortBy] = useState('startDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showDetailPanel, setShowDetailPanel] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockDelegations: Delegation[] = [
  {
    id: 'DEL-001',
    delegator: {
      name: 'Maria Santos',
      email: 'maria.santos@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10df5a971-1765003957966.png",
      alt: 'Professional woman with long brown hair in white blouse smiling at camera',
      department: 'Financeiro'
    },
    delegate: {
      name: 'João Silva',
      email: 'joao.silva@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12ef1a455-1763296313102.png",
      alt: 'Professional man with short dark hair in navy suit smiling confidently',
      department: 'Financeiro'
    },
    scope: 'Aprovação de despesas até R$ 10.000',
    reason: 'Férias programadas de 15 a 30 de dezembro',
    startDate: '15/12/2025',
    endDate: '30/12/2025',
    status: 'pending',
    type: 'Temporária - Férias',
    workloadImpact: 65
  },
  {
    id: 'DEL-002',
    delegator: {
      name: 'Carlos Oliveira',
      email: 'carlos.oliveira@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_124ddf771-1765167525499.png",
      alt: 'Professional man with glasses and beard in gray suit at office desk',
      department: 'Recursos Humanos'
    },
    delegate: {
      name: 'Ana Costa',
      email: 'ana.costa@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b24a7c2e-1763296129552.png",
      alt: 'Professional woman with blonde hair in black blazer smiling warmly',
      department: 'Recursos Humanos'
    },
    scope: 'Aprovação de contratações e processos seletivos',
    reason: 'Licença médica temporária',
    startDate: '08/12/2025',
    endDate: '22/12/2025',
    status: 'approved',
    type: 'Temporária - Licença Médica',
    approver: 'Diretor de RH',
    approvalDate: '07/12/2025',
    workloadImpact: 80
  },
  {
    id: 'DEL-003',
    delegator: {
      name: 'Patricia Lima',
      email: 'patricia.lima@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10df5a971-1765003957966.png",
      alt: 'Professional woman with curly hair in burgundy blazer smiling at camera',
      department: 'TI'
    },
    delegate: {
      name: 'Roberto Mendes',
      email: 'roberto.mendes@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ede8054b-1764653670188.png",
      alt: 'Professional man with short hair in blue shirt working at computer',
      department: 'TI'
    },
    scope: 'Aprovação de mudanças em produção',
    reason: 'Treinamento internacional',
    startDate: '10/12/2025',
    endDate: '20/12/2025',
    status: 'active',
    type: 'Temporária',
    approver: 'CTO',
    approvalDate: '09/12/2025',
    workloadImpact: 45
  },
  {
    id: 'DEL-004',
    delegator: {
      name: 'Fernando Souza',
      email: 'fernando.souza@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9ceddfc-1763299575596.png",
      alt: 'Professional man with short dark hair in white shirt smiling confidently',
      department: 'Operações'
    },
    delegate: {
      name: 'Juliana Rocha',
      email: 'juliana.rocha@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11956b05f-1763298649139.png",
      alt: 'Professional woman with straight hair in navy blazer at office',
      department: 'Operações'
    },
    scope: 'Aprovação de processos operacionais',
    reason: 'Promoção - transição de cargo',
    startDate: '01/12/2025',
    endDate: '31/12/2025',
    status: 'active',
    type: 'Permanente',
    approver: 'Diretor de Operações',
    approvalDate: '30/11/2025',
    workloadImpact: 90
  },
  {
    id: 'DEL-005',
    delegator: {
      name: 'Beatriz Alves',
      email: 'beatriz.alves@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9eab3f6-1763293440536.png",
      alt: 'Professional woman with long dark hair in white blouse smiling warmly',
      department: 'Vendas'
    },
    delegate: {
      name: 'Lucas Ferreira',
      email: 'lucas.ferreira@catalystbpm.com',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_124ddf771-1765167525499.png",
      alt: 'Professional man with short hair in gray suit at modern office',
      department: 'Vendas'
    },
    scope: 'Aprovação de propostas comerciais até R$ 50.000',
    reason: 'Viagem comercial internacional',
    startDate: '12/12/2025',
    endDate: '18/12/2025',
    status: 'pending',
    type: 'Temporária',
    workloadImpact: 55
  }];


  const [delegations, setDelegations] = useState<Delegation[]>(mockDelegations);

  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: 'all',
      delegationType: 'all',
      approvalStatus: 'all',
      department: 'all',
      savedView: ''
    });
  };

  const handleSelectDelegation = (id: string) => {
    setSelectedDelegations((prev) =>
    prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedDelegations(delegations.map((d) => d.id));
    } else {
      setSelectedDelegations([]);
    }
  };

  const handleViewDetails = (delegation: Delegation) => {
    setSelectedDelegation(delegation);
    setShowDetailPanel(true);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleApprove = (id: string, comment: string) => {
    setDelegations((prev) =>
    prev.map((d) => d.id === id ? { ...d, status: 'approved' as const } : d)
    );
    alert(`Delegação ${id} aprovada com sucesso!\nComentário: ${comment}`);
  };

  const handleReject = (id: string, comment: string) => {
    setDelegations((prev) =>
    prev.map((d) => d.id === id ? { ...d, status: 'rejected' as const } : d)
    );
    alert(`Delegação ${id} rejeitada.\nMotivo: ${comment}`);
  };

  const handleRevoke = (id: string, reason: string) => {
    setDelegations((prev) =>
    prev.map((d) => d.id === id ? { ...d, status: 'revoked' as const } : d)
    );
    alert(`Delegação ${id} revogada.\nMotivo: ${reason}`);
  };

  const handleExtend = (id: string, newEndDate: string) => {
    setDelegations((prev) =>
    prev.map((d) => d.id === id ? { ...d, endDate: newEndDate } : d)
    );
    alert(`Prazo da delegação ${id} estendido para ${newEndDate}`);
  };

  const handleBulkApprove = () => {
    const count = selectedDelegations.length;
    setDelegations((prev) =>
    prev.map((d) =>
    selectedDelegations.includes(d.id) ? { ...d, status: 'approved' as const } : d
    )
    );
    setSelectedDelegations([]);
    alert(`${count} delegações aprovadas em lote com sucesso!`);
  };

  const handleBulkReject = () => {
    const reason = prompt('Motivo da rejeição em lote:');
    if (reason) {
      const count = selectedDelegations.length;
      setDelegations((prev) =>
      prev.map((d) =>
      selectedDelegations.includes(d.id) ? { ...d, status: 'rejected' as const } : d
      )
      );
      setSelectedDelegations([]);
      alert(`${count} delegações rejeitadas em lote.\nMotivo: ${reason}`);
    }
  };

  const handleBulkExtend = () => {
    const newDate = prompt('Nova data de término (DD/MM/YYYY):');
    if (newDate) {
      const count = selectedDelegations.length;
      setDelegations((prev) =>
      prev.map((d) =>
      selectedDelegations.includes(d.id) ? { ...d, endDate: newDate } : d
      )
      );
      setSelectedDelegations([]);
      alert(`Prazo de ${count} delegações estendido para ${newDate}`);
    }
  };

  const handleBulkRevoke = () => {
    const reason = prompt('Motivo da revogação em lote:');
    if (reason) {
      const count = selectedDelegations.length;
      setDelegations((prev) =>
      prev.map((d) =>
      selectedDelegations.includes(d.id) ? { ...d, status: 'revoked' as const } : d
      )
      );
      setSelectedDelegations([]);
      alert(`${count} delegações revogadas em lote.\nMotivo: ${reason}`);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      alert(`Buscando por: ${searchQuery}`);
    }
  };

  useEffect(() => {
    if (!isHydrated) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (selectedDelegation && showDetailPanel) {
        if (e.key === 'a' || e.key === 'A') {
          if (selectedDelegation.status === 'pending') {
            handleApprove(selectedDelegation.id, 'Aprovação via atalho de teclado');
          }
        } else if (e.key === 'r' || e.key === 'R') {
          if (selectedDelegation.status === 'pending') {
            const reason = prompt('Motivo da rejeição:');
            if (reason) {
              handleReject(selectedDelegation.id, reason);
            }
          }
        } else if (e.key === 'e' || e.key === 'E') {
          if (selectedDelegation.status === 'approved' || selectedDelegation.status === 'active') {
            handleExtend(selectedDelegation.id, '31/12/2025');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isHydrated, selectedDelegation, showDetailPanel]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:357:6" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="357" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20p-6%22%7D" className="flex-1 p-6">
        <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:358:8" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="358" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
          <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:359:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="359" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-4%20gap-6%22%7D" className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) =>
            <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:361:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="361" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded-lg%22%7D" key={i} className="h-32 bg-muted rounded-lg" />
            )}
          </div>
          <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:364:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="364" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" className="h-96 bg-muted rounded-lg" />
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:371:4" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="371" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20flex-col%20lg%3Aflex-row%20gap-6%20p-6%22%7D" className="flex-1 flex flex-col lg:flex-row gap-6 p-6">
      <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:372:6" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="372" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20lg%3Aw-80%20flex-shrink-0%20space-y-6%22%7D" className="w-full lg:w-80 flex-shrink-0 space-y-6">
        <DelegationFilters data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:373:8" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="373" data-component-file="DelegationControlInteractive.tsx" data-component-name="DelegationFilters" data-component-content="%7B%22elementName%22%3A%22DelegationFilters%22%7D"
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters} />


        <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:379:8" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="379" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-surface%20border%20border-border%20rounded-lg%20p-4%22%7D" className="bg-surface border border-border rounded-lg p-4">
          <h3 data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:380:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="380" data-component-file="DelegationControlInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-foreground%20mb-3%20flex%20items-center%20gap-2%22%2C%22textContent%22%3A%22Atalhos%20de%20Teclado%22%7D" className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:381:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="381" data-component-file="DelegationControlInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22text-accent%22%7D" name="InformationCircleIcon" size={20} className="text-accent" />
            Atalhos de Teclado
          </h3>
          <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:384:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="384" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%20text-sm%20text-muted-foreground%22%7D" className="space-y-2 text-sm text-muted-foreground">
            <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:385:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="385" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
              <span data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:386:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="386" data-component-file="DelegationControlInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Aprovar%22%7D">Aprovar</span>
              <kbd data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:387:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="387" data-component-file="DelegationControlInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-2%20py-1%20bg-muted%20rounded%20text-xs%20font-mono%22%2C%22textContent%22%3A%22A%22%7D" className="px-2 py-1 bg-muted rounded text-xs font-mono">A</kbd>
            </div>
            <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:389:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="389" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
              <span data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:390:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="390" data-component-file="DelegationControlInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Rejeitar%22%7D">Rejeitar</span>
              <kbd data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:391:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="391" data-component-file="DelegationControlInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-2%20py-1%20bg-muted%20rounded%20text-xs%20font-mono%22%2C%22textContent%22%3A%22R%22%7D" className="px-2 py-1 bg-muted rounded text-xs font-mono">R</kbd>
            </div>
            <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:393:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="393" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
              <span data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:394:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="394" data-component-file="DelegationControlInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Estender%22%7D">Estender</span>
              <kbd data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:395:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="395" data-component-file="DelegationControlInteractive.tsx" data-component-name="kbd" data-component-content="%7B%22elementName%22%3A%22kbd%22%2C%22className%22%3A%22px-2%20py-1%20bg-muted%20rounded%20text-xs%20font-mono%22%2C%22textContent%22%3A%22E%22%7D" className="px-2 py-1 bg-muted rounded text-xs font-mono">E</kbd>
            </div>
          </div>
        </div>
      </div>

      <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:401:6" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="401" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20space-y-6%22%7D" className="flex-1 space-y-6">
        <DelegationStats data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:402:8" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="402" data-component-file="DelegationControlInteractive.tsx" data-component-name="DelegationStats" data-component-content="%7B%22elementName%22%3A%22DelegationStats%22%7D" />

        <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:404:8" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="404" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-surface%20border%20border-border%20rounded-lg%22%7D" className="bg-surface border border-border rounded-lg">
          <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:405:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="405" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20border-b%20border-border%22%7D" className="p-6 border-b border-border">
            <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:406:12" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="406" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20items-start%20lg%3Aitems-center%20justify-between%20gap-4%22%7D" className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:407:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="407" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <h2 data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:408:16" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="408" data-component-file="DelegationControlInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20font-semibold%20text-foreground%22%2C%22textContent%22%3A%22Delega%C3%A7%C3%B5es%20Ativas%22%7D" className="text-xl font-semibold text-foreground">Delegações Ativas</h2>
                <p data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:409:16" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="409" data-component-file="DelegationControlInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%2C%22textContent%22%3A%22Gerencie%20e%20aprove%20delega%C3%A7%C3%B5es%20de%20tarefas%22%7D" className="text-sm text-muted-foreground mt-1">
                  Gerencie e aprove delegações de tarefas
                </p>
              </div>

              <form data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:414:14" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="414" data-component-file="DelegationControlInteractive.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22relative%20w-full%20lg%3Aw-80%22%7D" onSubmit={handleSearch} className="relative w-full lg:w-80">
                <input data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:415:16" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="415" data-component-file="DelegationControlInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-4%20py-2%20bg-background%20border%20border-input%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar delegações..."
                  className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring" />

                <Icon data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:422:16" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="422" data-component-file="DelegationControlInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%20pointer-events-none%22%7D"
                  name="MagnifyingGlassIcon"
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />

              </form>
            </div>
          </div>

          <DelegationTable data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:431:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="431" data-component-file="DelegationControlInteractive.tsx" data-component-name="DelegationTable" data-component-content="%7B%22elementName%22%3A%22DelegationTable%22%7D"
            delegations={delegations}
            selectedDelegations={selectedDelegations}
            onSelectDelegation={handleSelectDelegation}
            onSelectAll={handleSelectAll}
            onViewDetails={handleViewDetails}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort} />

        </div>
      </div>

      {showDetailPanel &&
      <div data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:445:6" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="445" data-component-file="DelegationControlInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20lg%3Aw-%5B500px%5D%20flex-shrink-0%22%7D" className="w-full lg:w-[500px] flex-shrink-0">
          <DelegationDetailPanel data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:446:10" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="446" data-component-file="DelegationControlInteractive.tsx" data-component-name="DelegationDetailPanel" data-component-content="%7B%22elementName%22%3A%22DelegationDetailPanel%22%7D"
          delegation={selectedDelegation}
          onClose={() => setShowDetailPanel(false)}
          onApprove={handleApprove}
          onReject={handleReject}
          onRevoke={handleRevoke}
          onExtend={handleExtend} />

        </div>
      }

      <BulkActionsBar data-component-id="src/app/delegation-control-center/components/DelegationControlInteractive.tsx:457:6" data-component-path="src/app/delegation-control-center/components/DelegationControlInteractive.tsx" data-component-line="457" data-component-file="DelegationControlInteractive.tsx" data-component-name="BulkActionsBar" data-component-content="%7B%22elementName%22%3A%22BulkActionsBar%22%7D"
        selectedCount={selectedDelegations.length}
        onBulkApprove={handleBulkApprove}
        onBulkReject={handleBulkReject}
        onBulkExtend={handleBulkExtend}
        onBulkRevoke={handleBulkRevoke}
        onClearSelection={() => setSelectedDelegations([])} />

    </div>);

};

export default DelegationControlInteractive;